/**
 * DumpFileVisualizerTab — v4
 *
 * Layout:
 *  • Compact top bar: TO selector + vehicle chips (left) + Upload button (right)
 *  • Map (resizable via drag divider) + right panel (Stops / Segments)
 *  • Map overlays:
 *     – top-right: datetime filter, compute segments, GPS replay toggle, geofence toggles
 *     – bottom: GPS replay timeline bar (only when replay enabled)
 *  • Full-width GPS Coverage section below map
 */
import * as React from 'react'
import { useState, useRef, useEffect, useMemo } from 'react'
import {
    Circle,
    Map as LeafletMap,
    Marker,
    Polygon,
    Polyline,
    Popup,
    Tooltip,
    TileLayer,
} from 'react-leaflet'
import * as polylineCodec from '@mapbox/polyline'
import { divIcon, Icon as LeafletIcon } from 'leaflet'
import {
    Button,
    Dropdown,
    Header,
    Icon,
    Label,
    Loader,
    Message,
    Segment,
    Tab,
    Table,
} from 'semantic-ui-react'
import { TILE_SERVER_ACCESS_TOKEN, TILE_SERVER_URL, TILE_STYLE } from '../../../../../mapUtils'
import { backend } from '../../../../../xconvert-backend'
import { authentication } from '../../../../../authentication'
import { VehiclePolyline, VehicleTrackSegment, TourPolylineResponse } from '../../../../../generated'
import {
    DumpPosition,
    DumpStop,
    DumpTransportOrder,
    DumpVehicle,
    GeoStatusDump,
    GpsCoverageChunk,
    extractRadii,
    formatTs,
    parseMultiPolygonGeofence,
    getGeofenceCenter,
} from './dumpVisualizerUtils'
import { DumpGpsCoverageBar } from './DumpGpsCoverageBar'

// ─── Constants ────────────────────────────────────────────────────────────────

const VEHICLE_COLORS = ['#2185d0', '#e03997', '#21ba45', '#f2711c', '#a333c8', '#fbbd08']
const SELECTED_SEGMENT_COLOR = '#ff9900'
const MAP_HEIGHT = 580
const COVERAGE_MARKER_SIZE: [number, number] = [34, 48]
const COVERAGE_MARKER_ANCHOR: [number, number] = [17, 48]
const COVERAGE_MARKER_POPUP_ANCHOR: [number, number] = [0, -40]
const COVERAGE_BLOCK_MARKER_ICON = new LeafletIcon({
    iconUrl: '/assets/selected_position_marker.svg',
    iconSize: COVERAGE_MARKER_SIZE,
    iconAnchor: COVERAGE_MARKER_ANCHOR,
    popupAnchor: COVERAGE_MARKER_POPUP_ANCHOR,
})
const COVERAGE_SELECTED_MARKER_ICON = new LeafletIcon({
    iconUrl: '/assets/red_position_marker.svg',
    iconSize: COVERAGE_MARKER_SIZE,
    iconAnchor: COVERAGE_MARKER_ANCHOR,
    popupAnchor: COVERAGE_MARKER_POPUP_ANCHOR,
})

// ─── Helpers ──────────────────────────────────────────────────────────────────

function vehicleLabel(vid: string, vehicleMap: Map<string, DumpVehicle>): string {
    const v = vehicleMap.get(vid)
    if (!v) return vid.slice(0, 10) + '…'
    return v.numberPlate ?? v.externalVehicleId ?? vid.slice(0, 10) + '…'
}

function buildVehicleMap(dump: GeoStatusDump): Map<string, DumpVehicle> {
    const m = new Map<string, DumpVehicle>()
    ;(dump.vehicles ?? []).forEach(v => { if (v._id) m.set(v._id, v) })
    ;(dump.transportOrders ?? []).forEach(to => {
        if (to.assignedVehicle?._id) m.set(to.assignedVehicle._id, to.assignedVehicle)
        if (to.assignedTrailer?._id) m.set(to.assignedTrailer._id, to.assignedTrailer)
    })
    return m
}

function numberedStopIcon(n: number) {
    return divIcon({
        className: '',
        html: `<div style="background:#e03997;color:white;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:11px;border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,.5)">${n}</div>`,
        iconSize: [24, 24], iconAnchor: [12, 12],
    })
}

function truckIcon(heading?: number) {
    const rot = heading != null && Number.isFinite(heading) ? Math.round(heading) : 0
    return divIcon({
        className: '',
        html: `<div style="font-size:20px;line-height:1;transform:rotate(${rot}deg);filter:drop-shadow(0 1px 3px rgba(0,0,0,.5))">🚚</div>`,
        iconSize: [28, 28], iconAnchor: [14, 14],
    })
}

function toDateTimeLocalStr(d: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function isSameDumpPosition(a?: DumpPosition | null, b?: DumpPosition | null): boolean {
    if (!a || !b) return false
    if (a._id && b._id) return a._id === b._id
    return a.latitude === b.latitude && a.longitude === b.longitude && a.timestamp === b.timestamp
}

// ─── Main component ───────────────────────────────────────────────────────────

export const DumpFileVisualizerTab: React.FC = () => {

    // Core
    const [dump, setDump] = useState<GeoStatusDump | null>(null)
    const [fileName, setFileName] = useState('')
    const [error, setError] = useState<string | null>(null)

    // Selection
    const [selectedToId, setSelectedToId] = useState<string | null>(null)
    const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>([])

    // DateTime filter
    const [filterFrom, setFilterFrom] = useState<string>('')
    const [filterTo, setFilterTo] = useState<string>('')
    const [appliedFilter, setAppliedFilter] = useState<{ from: Date; to: Date } | null>(null)

    // Playback
    const [playbackIndex, setPlaybackIndex] = useState(0)
    const [isPlaying, setIsPlaying] = useState(false)
    const [playbackSpeed, setPlaybackSpeed] = useState(1)
    const [showReplay, setShowReplay] = useState(false)
    const playIntervalRef = useRef<any>(null)

    // Map interaction
    const [selectedBackendSegIdx, setSelectedBackendSegIdx] = useState<number | null>(null)
    const [flyToPosition, setFlyToPosition] = useState<DumpPosition | null>(null)
    const mapRef = useRef<any>(null)

    // Geofence
    const [showArrivalRadius, setShowArrivalRadius] = useState(true)
    const [showDepartureRadius, setShowDepartureRadius] = useState(false)
    const [showEarlyArrivalRadius, setShowEarlyArrivalRadius] = useState(true)
    const [showGeofence, setShowGeofence] = useState(true)

    // Backend segments
    const [backendVehiclePolylines, setBackendVehiclePolylines] = useState<VehiclePolyline[]>([])
    const [segmentsLoading, setSegmentsLoading] = useState(false)
    const [segmentsFetchError, setSegmentsFetchError] = useState<string | null>(null)

    // GPS Coverage
    const [coveragePositions, setCoveragePositions] = useState<DumpPosition[]>([])
    const [coverageChunk, setCoverageChunk] = useState<GpsCoverageChunk | null>(null)
    const [coverageChunkMinutes, setCoverageChunkMinutes] = useState<number>(15)
    const [selectedCoveragePosition, setSelectedCoveragePosition] = useState<DumpPosition | null>(null)

    // UI
    const [rightTabIdx, setRightTabIdx] = useState(0)
    const [mapWidthPct, setMapWidthPct] = useState(65)
    const containerRef = useRef<HTMLDivElement>(null)
    const isDraggingRef = useRef(false)

    // ── Derived ────────────────────────────────────────────────────────────────

    const vehicleMap = useMemo(() => (dump ? buildVehicleMap(dump) : new Map<string, DumpVehicle>()), [dump])
    const radii = useMemo(() => (dump ? extractRadii(dump) : {}), [dump])

    const selectedTo: DumpTransportOrder | null = useMemo(
        () => dump?.transportOrders?.find(to => to._id === selectedToId) ?? null,
        [dump, selectedToId]
    )

    const allStops: DumpStop[] = useMemo(
        () => selectedTo?.transports?.flatMap(t => t.stops ?? []) ?? [],
        [selectedTo]
    )

    const vehicleIdsInPositions: string[] = useMemo(
        () => [...new Set((dump?.positions ?? []).map(p => p.vehicleId).filter(Boolean) as string[])],
        [dump]
    )

    const vehiclePositions = useMemo(
        () => selectedVehicleIds.map((vid, ci) => ({
            vehicleId: vid,
            color: VEHICLE_COLORS[ci % VEHICLE_COLORS.length],
            positions: (dump?.positions ?? [])
                .filter(p => {
                    if (p.vehicleId !== vid || p.latitude == null || p.longitude == null) return false
                    if (!appliedFilter) return true
                    const ts = p.timestamp ? new Date(p.timestamp).getTime() : 0
                    return ts >= appliedFilter.from.getTime() && ts <= appliedFilter.to.getTime()
                })
                .sort((a, b) => new Date(a.timestamp ?? 0).getTime() - new Date(b.timestamp ?? 0).getTime()),
        })),
        [dump, selectedVehicleIds, appliedFilter]
    )

    const allSelectedPositions: DumpPosition[] = useMemo(
        () => vehiclePositions.flatMap(vp => vp.positions),
        [vehiclePositions]
    )

    const allBackendSegments: (VehicleTrackSegment & { _vidIdx: number })[] = useMemo(() => {
        const result: (VehicleTrackSegment & { _vidIdx: number })[] = []
        backendVehiclePolylines.forEach((vp, vi) => {
            ;(vp.vehicleTrackSegments ?? []).forEach(seg => result.push({ ...seg, _vidIdx: vi }))
        })
        return result.sort((a, b) => {
            const ta = a.from ? new Date(a.from).getTime() : 0
            const tb = b.from ? new Date(b.from).getTime() : 0
            return ta - tb
        })
    }, [backendVehiclePolylines])

    const maxSliderVal = Math.max(0, ...vehiclePositions.map(vp => Math.max(0, vp.positions.length - 1)))

    const primaryCurrentTs =
        vehiclePositions[0]?.positions[Math.min(playbackIndex, Math.max(0, vehiclePositions[0]?.positions.length - 1))]?.timestamp

    const coverageTimeRange = useMemo((): { from: Date; to: Date } | null => {
        const times = allSelectedPositions
            .map(p => p.timestamp ? new Date(p.timestamp).getTime() : null)
            .filter(t => t != null && !isNaN(t as number)) as number[]
        if (times.length === 0) return null
        return { from: new Date(Math.min(...times)), to: new Date(Math.max(...times)) }
    }, [allSelectedPositions])

    // ── Effects ────────────────────────────────────────────────────────────────

    useEffect(() => {
        fitMapBounds()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedToId, selectedVehicleIds.join(','), appliedFilter, dump])

    function fitMapBounds() {
        const coords: [number, number][] = []
        vehiclePositions.forEach(vp =>
            vp.positions.forEach(p => { if (p.latitude != null && p.longitude != null) coords.push([p.latitude, p.longitude]) })
        )
        allStops.forEach(s => {
            if (s.coordinates?.latitude != null && s.coordinates?.longitude != null)
                coords.push([s.coordinates.latitude, s.coordinates.longitude])
        })
        if (coords.length > 0 && mapRef.current?.leafletElement) {
            const lats = coords.map(c => c[0]), lngs = coords.map(c => c[1])
            try { mapRef.current.leafletElement.fitBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]], { padding: [40, 40] }) } catch (_) {}
        }
    }

    useEffect(() => {
        if (flyToPosition?.latitude != null && flyToPosition.longitude != null && mapRef.current?.leafletElement) {
            try { mapRef.current.leafletElement.flyTo([flyToPosition.latitude, flyToPosition.longitude], 16) } catch (_) {}
        }
    }, [flyToPosition])

    useEffect(() => {
        // removed: client-side segment fly-to (segments come from backend now)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Fit map bounds whenever a GPS coverage block is selected
    useEffect(() => {
        if (coveragePositions.length === 0) return
        const coords = coveragePositions
            .filter(p => p.latitude != null && p.longitude != null)
            .map(p => [p.latitude!, p.longitude!] as [number, number])
        if (coords.length === 0 || !mapRef.current?.leafletElement) return
        const lats = coords.map(c => c[0])
        const lngs = coords.map(c => c[1])
        try {
            mapRef.current.leafletElement.fitBounds(
                [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
                { padding: [40, 40] }
            )
        } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coveragePositions])

    useEffect(() => {
        if (!selectedCoveragePosition) return
        const stillVisible = coveragePositions.some(p => isSameDumpPosition(p, selectedCoveragePosition))
        if (!stillVisible) setSelectedCoveragePosition(null)
    }, [coveragePositions, selectedCoveragePosition])

    // Auto-fetch segments whenever the set of positions changes (dump load, TO switch, filter applied)
    useEffect(() => {
        if (allSelectedPositions.length === 0) return
        void fetchBackendSegments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allSelectedPositions])

    useEffect(() => {
        if (!selectedTo) return
        const stops = selectedTo.transports?.flatMap(t => t.stops ?? []) ?? []
        const arriveTimes = stops.map(s => s.planArrive).filter(Boolean).map(t => new Date(t!).getTime()).filter(t => !isNaN(t))
        const departTimes = stops.map(s => s.planDepart).filter(Boolean).map(t => new Date(t!).getTime()).filter(t => !isNaN(t))
        if (arriveTimes.length > 0 && departTimes.length > 0) {
            const from = new Date(Math.min(...arriveTimes))
            const to = new Date(Math.max(...departTimes))
            setFilterFrom(toDateTimeLocalStr(from))
            setFilterTo(toDateTimeLocalStr(to))
            // Auto-apply immediately so positions are filtered by default
            setAppliedFilter({ from, to })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedToId, dump])

    useEffect(() => {
        clearInterval(playIntervalRef.current)
        if (isPlaying) {
            playIntervalRef.current = setInterval(() => {
                setPlaybackIndex(prev => {
                    if (prev >= maxSliderVal) { setIsPlaying(false); return prev }
                    return prev + 1
                })
            }, 1000 / playbackSpeed)
        }
        return () => clearInterval(playIntervalRef.current)
    }, [isPlaying, playbackSpeed, maxSliderVal])

    // ── Resize divider ─────────────────────────────────────────────────────────

    function handleDividerMouseDown(e: React.MouseEvent) {
        e.preventDefault()
        isDraggingRef.current = true
        const startX = e.clientX
        const startPct = mapWidthPct
        const containerWidth = containerRef.current?.getBoundingClientRect().width ?? 1200

        function onMouseMove(e: MouseEvent) {
            if (!isDraggingRef.current) return
            const delta = e.clientX - startX
            setMapWidthPct(Math.max(30, Math.min(85, startPct + (delta / containerWidth) * 100)))
        }
        function onMouseUp() {
            isDraggingRef.current = false
            document.removeEventListener('mousemove', onMouseMove)
            document.removeEventListener('mouseup', onMouseUp)
            setTimeout(() => { try { mapRef.current?.leafletElement?.invalidateSize() } catch (_) {} }, 100)
        }
        document.addEventListener('mousemove', onMouseMove)
        document.addEventListener('mouseup', onMouseUp)
    }

    // ── Handlers ───────────────────────────────────────────────────────────────

    function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
        setError(null)
        const file = e.target.files?.[0]
        if (!file) return
        setFileName(file.name)
        const reader = new FileReader()
        reader.onload = ev => {
            try {
                const parsed: GeoStatusDump = JSON.parse(ev.target?.result as string)
                setDump(parsed)
                initSelections(parsed)
                setBackendVehiclePolylines([])
                setCoveragePositions([])
                setCoverageChunk(null)
                setSelectedCoveragePosition(null)
            } catch (err) { setError('Failed to parse JSON: ' + String(err)) }
        }
        reader.readAsText(file)
        e.target.value = ''
    }

    function initSelections(parsed: GeoStatusDump) {
        setPlaybackIndex(0); setIsPlaying(false); setAppliedFilter(null)
        const firstTo = parsed.transportOrders?.[0] ?? null
        setSelectedToId(firstTo?._id ?? null)
        const auto: string[] = []
        if (firstTo?.assignedVehicle?._id) auto.push(firstTo.assignedVehicle._id)
        if (firstTo?.assignedTrailer?._id) auto.push(firstTo.assignedTrailer._id)
        const posVids = [...new Set((parsed.positions ?? []).map(p => p.vehicleId).filter(Boolean) as string[])]
        setSelectedVehicleIds(auto.length > 0 ? auto : posVids.slice(0, 2))
    }

    function handleToChange(toId: string) {
        setSelectedToId(toId); setPlaybackIndex(0); setIsPlaying(false)
        setBackendVehiclePolylines([]); setAppliedFilter(null)
        setCoveragePositions([]); setCoverageChunk(null); setSelectedCoveragePosition(null)
        const to = dump?.transportOrders?.find(t => t._id === toId)
        if (to) {
            const auto: string[] = []
            if (to.assignedVehicle?._id) auto.push(to.assignedVehicle._id)
            if (to.assignedTrailer?._id) auto.push(to.assignedTrailer._id)
            setSelectedVehicleIds(auto.length > 0 ? auto : vehicleIdsInPositions.slice(0, 2))
        }
    }

    function toggleVehicle(vid: string) {
        setPlaybackIndex(0); setIsPlaying(false)
        setCoveragePositions([]); setCoverageChunk(null); setSelectedCoveragePosition(null)
        setSelectedVehicleIds(prev => prev.includes(vid) ? prev.filter(v => v !== vid) : [...prev, vid])
    }

    function applyFilter() {
        setPlaybackIndex(0); setIsPlaying(false)
        setBackendVehiclePolylines([]); setSelectedBackendSegIdx(null)
        setCoveragePositions([]); setCoverageChunk(null); setSelectedCoveragePosition(null)
        const newFilter = filterFrom && filterTo
            ? { from: new Date(filterFrom), to: new Date(filterTo) }
            : null
        setAppliedFilter(newFilter)
    }

    async function fetchBackendSegments() {
        const positionsToSend = vehiclePositions.flatMap(vp => vp.positions)
        if (positionsToSend.length === 0) return
        setSegmentsLoading(true); setSegmentsFetchError(null); setBackendVehiclePolylines([])
        try {
            const auth = backend.withTokenAuthHeader(authentication.token)
            const response = await fetch('/api/internal/dump/computeSegmentsFromPositions', {
                method: 'POST',
                headers: { Authorization: auth.headers.Authorization.toString(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ positions: positionsToSend }),
            })
            if (!response.ok) throw new Error(`HTTP ${response.status}`)
            const data: TourPolylineResponse = await response.json()
            setBackendVehiclePolylines(data.vehiclePolylines ?? [])
            setRightTabIdx(1)
        } catch (e) {
            setSegmentsFetchError('Something went wrong while fetching segments. (' + String(e) + ')')
        } finally {
            setSegmentsLoading(false)
        }
    }

    function handleBackendSegmentClick(idx: number, seg: VehicleTrackSegment) {
        setSelectedBackendSegIdx(prev => prev === idx ? null : idx)
        if (seg.polyline && mapRef.current?.leafletElement) {
            try {
                const coords = polylineCodec.decode(seg.polyline)
                if (coords.length >= 2) {
                    const lats = coords.map(c => c[0]), lngs = coords.map(c => c[1])
                    mapRef.current.leafletElement.fitBounds([[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]], { padding: [60, 60] })
                }
            } catch (_) {}
        }
    }

    // ── Render: top bar ────────────────────────────────────────────────────────

    function renderTopBar() {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
                {/* Left: TO + vehicles */}
                {dump && (
                    <>
                        <Dropdown
                            selection
                            compact
                            options={(dump.transportOrders ?? []).map(to => ({
                                key: to._id, value: to._id,
                                text: `${to._id}${to.externalTransportOrderId ? ` (${to.externalTransportOrderId})` : ''}`,
                            }))}
                            value={selectedToId ?? undefined}
                            onChange={(_, { value }) => handleToChange(value as string)}
                            style={{ minWidth: 200, fontSize: '0.88em' }}
                        />
                        {vehicleIdsInPositions.map((vid, idx) => {
                            const isSelected = selectedVehicleIds.includes(vid)
                            const color = VEHICLE_COLORS[idx % VEHICLE_COLORS.length]
                            return (
                                <Label
                                    key={vid}
                                    size="small"
                                    style={{ cursor: 'pointer', background: isSelected ? color : '#ccc', color: 'white', opacity: isSelected ? 1 : 0.5 }}
                                    onClick={() => toggleVehicle(vid)}
                                >
                                    {selectedTo?.assignedVehicle?._id === vid ? '🚚 ' : selectedTo?.assignedTrailer?._id === vid ? '🚛 ' : ''}
                                    {vehicleLabel(vid, vehicleMap)}
                                    <Label.Detail>{(dump.positions ?? []).filter(p => p.vehicleId === vid).length}</Label.Detail>
                                </Label>
                            )
                        })}
                        {appliedFilter && (
                            <Label size="small" color="blue">
                                <Icon name="filter" />{allSelectedPositions.length} pts filtered
                            </Label>
                        )}
                    </>
                )}

                <div style={{ flex: 1 }} />

                {/* Right: upload */}
                <Button as="label" htmlFor="dv-file-input" primary icon labelPosition="left" size="small">
                    <Icon name="upload" />Upload Dump JSON
                </Button>
                <input id="dv-file-input" type="file" accept=".json" style={{ display: 'none' }} onChange={handleFileUpload} />
                {dump && (
                    <span style={{ color: '#21ba45', fontSize: '0.88em', whiteSpace: 'nowrap' }}>
                        ✅ <strong>{fileName}</strong>
                    </span>
                )}
            </div>
        )
    }

    // ── Render: map control overlay (top-right) ────────────────────────────────

    function renderControlOverlay() {
        if (!dump) return null
        return (
            <div
                style={{
                    background: 'rgba(255,255,255,0.96)',
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    padding: '8px 10px',
                    minWidth: 220,
                    maxWidth: 260,
                    fontSize: '0.82em',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                }}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
            >
                {/* DateTime filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                    <span style={{ width: 34, color: '#555' }}>From</span>
                    <input
                        type="datetime-local"
                        value={filterFrom}
                        onChange={e => setFilterFrom(e.target.value)}
                        style={{ flex: 1, fontSize: '0.9em', padding: '2px 4px', border: '1px solid #ccc', borderRadius: 2 }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
                    <span style={{ width: 34, color: '#555' }}>Until</span>
                    <input
                        type="datetime-local"
                        value={filterTo}
                        onChange={e => setFilterTo(e.target.value)}
                        style={{ flex: 1, fontSize: '0.9em', padding: '2px 4px', border: '1px solid #ccc', borderRadius: 2 }}
                    />
                </div>
                <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
                    <button
                        onClick={applyFilter}
                        style={{ flex: 1, padding: '3px 6px', background: '#2185d0', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '0.9em' }}
                    >
                        Apply
                    </button>
                    {appliedFilter && (
                        <button
                            onClick={() => {
                                setAppliedFilter(null)
                                setBackendVehiclePolylines([])
                                setCoveragePositions([])
                                setCoverageChunk(null)
                                setSelectedCoveragePosition(null)
                            }}
                            style={{ padding: '3px 6px', background: '#eee', border: '1px solid #ccc', borderRadius: 3, cursor: 'pointer', fontSize: '0.9em' }}
                        >
                            Clear
                        </button>
                    )}
                    <button
                        onClick={fetchBackendSegments}
                        disabled={segmentsLoading || allSelectedPositions.length === 0}
                        style={{ padding: '3px 6px', background: '#00b5ad', color: 'white', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '0.9em', opacity: allSelectedPositions.length === 0 ? 0.5 : 1 }}
                        title="Compute DRIVING/STANDING segments"
                    >
                        {segmentsLoading ? '…' : '📊 Seg'}
                    </button>
                </div>

                <div style={{ borderTop: '1px solid #eee', paddingTop: 6, marginBottom: 4 }}>
                    {/* GPS replay toggle */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 4 }}>
                        <input
                            type="checkbox"
                            checked={showReplay}
                            onChange={e => { setShowReplay(e.target.checked); if (!e.target.checked) setIsPlaying(false) }}
                        />
                        GPS route replay
                    </label>
                </div>

                {/* Geofence radii toggles */}
                {(radii.arrivalRadiusInMeter || radii.departureRadiusInMeter || radii.earlyArrivalRadiusInMeter) && (
                    <div style={{ borderTop: '1px solid #eee', paddingTop: 6 }}>
                        {radii.arrivalRadiusInMeter && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 3 }}>
                                <input type="checkbox" checked={showArrivalRadius} onChange={e => setShowArrivalRadius(e.target.checked)} />
                                <span style={{ display: 'inline-block', width: 10, height: 10, background: '#2185d0', borderRadius: 2 }}></span>
                                Arrival Radius ({radii.arrivalRadiusInMeter}m)
                            </label>
                        )}
                        {radii.departureRadiusInMeter && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 3 }}>
                                <input type="checkbox" checked={showDepartureRadius} onChange={e => setShowDepartureRadius(e.target.checked)} />
                                <span style={{ display: 'inline-block', width: 10, height: 10, background: '#21ba45', borderRadius: 2 }}></span>
                                Departure Radius ({radii.departureRadiusInMeter}m)
                            </label>
                        )}
                        {radii.earlyArrivalRadiusInMeter && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                                <input type="checkbox" checked={showEarlyArrivalRadius} onChange={e => setShowEarlyArrivalRadius(e.target.checked)} />
                                <span style={{ display: 'inline-block', width: 10, height: 10, background: '#54c8ff', borderRadius: 2 }}></span>
                                Early Arrival ({radii.earlyArrivalRadiusInMeter}m)
                            </label>
                        )}
                    </div>
                )}
                {/* Geofence polygon toggle — shown if any stop has a polygon geofence */}
                {allStops.some(s => !!s.multiPolygonGeofence) && (
                    <div style={{ borderTop: '1px solid #eee', paddingTop: 6 }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                            <input type="checkbox" checked={showGeofence} onChange={e => setShowGeofence(e.target.checked)} />
                            <span style={{ display: 'inline-block', width: 10, height: 10, background: '#f2711c', borderRadius: 2 }}></span>
                            Geofences ({allStops.filter(s => !!s.multiPolygonGeofence).length} stops)
                        </label>
                    </div>
                )}

                {segmentsFetchError && (
                    <div style={{ marginTop: 4, color: '#db2828', fontSize: '0.82em', borderTop: '1px solid #eee', paddingTop: 4 }}>
                        <Icon name="warning sign" />{segmentsFetchError.split('(')[0]}
                    </div>
                )}
            </div>
        )
    }

    // ── Render: playback overlay (bottom of map, dark blue) ────────────────────

    function renderPlaybackOverlay() {
        if (!showReplay || maxSliderVal === 0) return null
        const startTs = vehiclePositions[0]?.positions[0]?.timestamp
        const endTs = vehiclePositions[0]?.positions[vehiclePositions[0].positions.length - 1]?.timestamp
        const timeLabel = (ts?: string) => ts ? new Date(ts).toTimeString().slice(0, 5) : '—'

        return (
            <div
                style={{
                    background: 'rgba(10,25,55,0.93)',
                    color: 'white',
                    padding: '6px 14px 8px',
                    borderTop: '2px solid #2185d0',
                }}
                onMouseDown={e => e.stopPropagation()}
                onClick={e => e.stopPropagation()}
            >
                <div style={{ fontSize: '0.68em', color: '#7ab3e8', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Timeline Overlays
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                    <button
                        onClick={() => setIsPlaying(p => !p)}
                        style={{ background: isPlaying ? '#db2828' : '#21ba45', border: 'none', borderRadius: 3, color: 'white', padding: '3px 8px', cursor: 'pointer', fontSize: '1em' }}
                    >
                        {isPlaying ? '⏸' : '▶'}
                    </button>
                    <div style={{ display: 'flex', gap: 2 }}>
                        {[1, 2, 4, 8].map(s => (
                            <button
                                key={s}
                                onClick={() => setPlaybackSpeed(s)}
                                style={{
                                    background: playbackSpeed === s ? '#2185d0' : 'rgba(255,255,255,0.15)',
                                    border: 'none', borderRadius: 3, color: 'white',
                                    padding: '2px 6px', cursor: 'pointer', fontSize: '0.82em',
                                }}
                            >
                                {s}×
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => fitMapBounds()}
                        style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 3, color: 'white', padding: '2px 8px', cursor: 'pointer', fontSize: '0.82em' }}
                        title="Follow truck"
                    >
                        📍 Follow
                    </button>
                    <span style={{ fontSize: '0.85em', color: '#7ab3e8', marginLeft: 4 }}>
                        {primaryCurrentTs ? new Date(primaryCurrentTs).toISOString().replace('T', ' ').slice(0, 19) + ' UTC' : '—'}
                    </span>
                </div>

                {/* Timeline slider */}
                <div style={{ position: 'relative', paddingBottom: 18 }}>
                    <input
                        type="range" min={0} max={maxSliderVal} value={playbackIndex}
                        style={{ width: '100%' }}
                        onChange={e => { setPlaybackIndex(Number(e.target.value)); setIsPlaying(false) }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72em', color: '#7ab3e8', position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                        <span>{timeLabel(startTs)}</span>
                        <span>{timeLabel(endTs)}</span>
                    </div>
                </div>

                {/* Legend */}
                <div style={{ display: 'flex', gap: 16, fontSize: '0.72em', color: '#aac8e8' }}>
                    <span><span style={{ display: 'inline-block', width: 14, height: 3, background: '#4488ff', verticalAlign: 'middle', marginRight: 4 }}></span>Moving</span>
                    <span><span style={{ display: 'inline-block', width: 14, height: 3, background: '#db2828', verticalAlign: 'middle', marginRight: 4 }}></span>Standing</span>
                    <span style={{ color: '#888' }}>Playback uses GPS points. Numbered markers = stops.</span>
                </div>
            </div>
        )
    }

    // ── Render: map ────────────────────────────────────────────────────────────

    function renderMap() {
        return (
            <div style={{ position: 'relative', height: MAP_HEIGHT }}>
                <LeafletMap ref={mapRef} center={[51.0, 10.0]} zoom={7} style={{ height: '100%', width: '100%' }}>
                    <TileLayer url={TILE_SERVER_URL} /* @ts-ignore */ id={TILE_STYLE} accessToken={TILE_SERVER_ACCESS_TOKEN} attribution="© Mapbox" />

                    {/* Raw GPS track polylines */}
                    {vehiclePositions.map(({ vehicleId, positions, color }) => {
                        if (positions.length < 2) return null
                        return <Polyline key={`track-${vehicleId}`} positions={positions.map(p => [p.latitude!, p.longitude!] as [number, number])} color={color} weight={3} opacity={0.6} />
                    })}

                    {/* Backend segment polylines */}
                    {allBackendSegments.map((seg, idx) => {
                        if (!seg.polyline) return null
                        try {
                            const coords = polylineCodec.decode(seg.polyline)
                            if (coords.length < 2) return null
                            const isDriving = seg.estimatedState === VehicleTrackSegment.EstimatedStateEnum.DRIVING
                            const isSelected = selectedBackendSegIdx === idx
                            return (
                                <Polyline
                                    key={`bseg-${idx}`}
                                    positions={coords as [number, number][]}
                                    color={isSelected ? SELECTED_SEGMENT_COLOR : isDriving ? '#2185d0' : '#999'}
                                    weight={isSelected ? 7 : isDriving ? 4 : 3}
                                    opacity={isSelected ? 1 : 0.8}
                                />
                            )
                        } catch (_) { return null }
                    })}

                    {/* Client-side selected segment — removed; segments come from backend only */}

                    {/* Stop markers + geofence circles + polygon geofences */}
                    {allStops.map((stop, idx) => {
                        const lat = stop.coordinates?.latitude, lng = stop.coordinates?.longitude
                        const hasCoords = lat != null && lng != null
                        const geofenceRings = parseMultiPolygonGeofence(stop.multiPolygonGeofence)

                        // Prefer geofence centroid as marker position; fall back to coordinates
                        const geofenceCenter = geofenceRings ? getGeofenceCenter(geofenceRings) : null
                        const markerPos: [number, number] | null = geofenceCenter ?? (hasCoords ? [lat!, lng!] : null)

                        if (!markerPos && !geofenceRings) return null

                        return (
                            <React.Fragment key={`stop-group-${stop.stopId ?? stop.mergeAndOrderKey ?? ''}-${idx}`}>
                                {markerPos && (
                                    <Marker position={markerPos} icon={numberedStopIcon(idx + 1)}>
                                        <Popup>
                                            <strong>{idx + 1}. {stop.name}</strong>{stop.name2 && <> / {stop.name2}</>}<br />
                                            {[stop.street, stop.zipCode, stop.city, stop.countryCode].filter(Boolean).join(', ')}<br />
                                            Plan arrive: {formatTs(stop.planArrive)}<br />Plan depart: {formatTs(stop.planDepart)}
                                        </Popup>
                                    </Marker>
                                )}
                                {/* Radius circles — only when there is no polygon geofence for this stop */}
                                {!geofenceRings && hasCoords && (
                                    <>
                                        {showArrivalRadius && radii.arrivalRadiusInMeter && (
                                            <Circle center={[lat!, lng!]} radius={radii.arrivalRadiusInMeter} color="#2185d0" fillColor="#2185d0" fillOpacity={0.08} weight={1} />
                                        )}
                                        {showDepartureRadius && radii.departureRadiusInMeter && (
                                            <Circle center={[lat!, lng!]} radius={radii.departureRadiusInMeter} color="#21ba45" fillColor="#21ba45" fillOpacity={0.06} weight={1} />
                                        )}
                                        {showEarlyArrivalRadius && radii.earlyArrivalRadiusInMeter && (
                                            <Circle center={[lat!, lng!]} radius={radii.earlyArrivalRadiusInMeter} color="#54c8ff" fillColor="#54c8ff" fillOpacity={0.04} weight={1} />
                                        )}
                                    </>
                                )}
                                {/* Polygon geofence rings */}
                                {showGeofence && geofenceRings && geofenceRings.map((ring, ringIdx) => (
                                    <Polygon
                                        key={`geofence-${idx}-${ringIdx}`}
                                        positions={ring}
                                        color="#f2711c"
                                        fillColor="#f2711c"
                                        fillOpacity={0.12}
                                        weight={2}
                                    >
                                        <Popup>
                                            <strong>{idx + 1}. {stop.name ?? 'Stop'}</strong><br />
                                            Geofence ring {ringIdx + 1}/{geofenceRings.length} · {ring.length} vertices
                                        </Popup>
                                    </Polygon>
                                ))}
                            </React.Fragment>
                        )
                    })}

                    {/* Playback truck */}
                    {showReplay && vehiclePositions.map(({ vehicleId, positions }) => {
                        if (positions.length === 0) return null
                        const idx = Math.min(playbackIndex, positions.length - 1)
                        const p = positions[idx]
                        if (!p || p.latitude == null || p.longitude == null) return null
                        return (
                            <Marker key={`truck-${vehicleId}`} position={[p.latitude, p.longitude]} icon={truckIcon(p.heading)}>
                                <Popup>
                                    <strong>🚚 {vehicleLabel(vehicleId, vehicleMap)}</strong><br />
                                    {formatTs(p.timestamp)}<br />
                                    Speed: {p.speed != null ? p.speed.toFixed(1) + ' km/h' : '—'} · Heading: {p.heading != null ? p.heading.toFixed(0) + '°' : '—'}
                                </Popup>
                            </Marker>
                        )
                    })}

                    {/* Coverage highlighted positions — capped to 300 to avoid crashing Leaflet */}
                    {coveragePositions.filter(p => p.latitude != null && p.longitude != null).slice(0, 300).map((p, i) => (
                        <Marker
                             key={`cov-${i}`}
                             position={[p.latitude!, p.longitude!]}
                             icon={COVERAGE_BLOCK_MARKER_ICON}
                             opacity={isSameDumpPosition(p, selectedCoveragePosition) ? 0 : 1}
                             onClick={() => {
                                 setSelectedCoveragePosition(p)
                                 setFlyToPosition(p)
                             }}
                         >
                             <Tooltip direction="top" offset={[0, -46]} opacity={0.92}>
                                 <span style={{ fontSize: '0.82em' }}>
                                     {formatTs(p.timestamp)}<br />
                                     {p.latitude!.toFixed(6)}, {p.longitude!.toFixed(6)}<br />
                                     Speed: {p.speed != null ? p.speed.toFixed(1) + ' km/h' : '—'}
                                 </span>
                             </Tooltip>
                         </Marker>
                    ))}

                    {/* Selected coverage position marker overlays regular coverage markers */}
                    {selectedCoveragePosition?.latitude != null && selectedCoveragePosition.longitude != null && (
                        <Marker
                             position={[selectedCoveragePosition.latitude, selectedCoveragePosition.longitude]}
                             icon={COVERAGE_SELECTED_MARKER_ICON}
                             zIndexOffset={1000}
                             onClick={() => setSelectedCoveragePosition(null)}
                         >
                             <Tooltip direction="top" offset={[0, -46]} opacity={0.92} permanent={false}>
                                 <span style={{ fontSize: '0.82em' }}>
                                     ✅ {formatTs(selectedCoveragePosition.timestamp)}<br />
                                     {selectedCoveragePosition.latitude.toFixed(6)}, {selectedCoveragePosition.longitude.toFixed(6)}<br />
                                     Speed: {selectedCoveragePosition.speed != null ? selectedCoveragePosition.speed.toFixed(1) + ' km/h' : '—'}
                                     <br /><span style={{ color: '#888' }}>click to deselect</span>
                                 </span>
                             </Tooltip>
                         </Marker>
                    )}
                </LeafletMap>

                {/* Top-right overlay: controls */}
                <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 1000, pointerEvents: 'auto' }}>
                    {renderControlOverlay()}
                </div>

                {/* Bottom overlay: playback timeline */}
                {showReplay && (
                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 1000, pointerEvents: 'auto' }}>
                        {renderPlaybackOverlay()}
                    </div>
                )}
            </div>
        )
    }

    // ── Render: right panel tabs ───────────────────────────────────────────────

    function renderStopsTab() {
        if (allStops.length === 0) return <Message info size="small"><p>Select a transport order.</p></Message>
        const hasCoords = allStops.some(s => s.coordinates?.latitude != null)
        return (
            <>
                {!hasCoords && <Message warning size="small" compact><Icon name="map marker alternate" />No coordinates — stops not on map.</Message>}
                <div style={{ overflowY: 'auto', maxHeight: MAP_HEIGHT - 60 }}>
                    <Table compact size="small" striped celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>#</Table.HeaderCell>
                                <Table.HeaderCell>Name / Address</Table.HeaderCell>
                                <Table.HeaderCell>Plan Arrive</Table.HeaderCell>
                                <Table.HeaderCell>Plan Depart</Table.HeaderCell>
                                <Table.HeaderCell>Actions</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {allStops.map((stop, idx) => (
                                <Table.Row key={`stop-row-${stop.stopId ?? stop.mergeAndOrderKey ?? ''}-${idx}`}>
                                    <Table.Cell><Label circular size="small" style={{ background: '#e03997', color: 'white' }}>{idx + 1}</Label></Table.Cell>
                                    <Table.Cell>
                                        <strong style={{ fontSize: '0.88em' }}>{stop.name}</strong>
                                        {stop.name2 && <span style={{ color: '#888', fontSize: '0.82em' }}> / {stop.name2}</span>}<br />
                                        <span style={{ fontSize: '0.78em', color: '#666' }}>{[stop.street, stop.zipCode, stop.city, stop.countryCode].filter(Boolean).join(', ')}</span>
                                    </Table.Cell>
                                    <Table.Cell style={{ fontSize: '0.78em', whiteSpace: 'nowrap' }}>{formatTs(stop.planArrive)}</Table.Cell>
                                    <Table.Cell style={{ fontSize: '0.78em', whiteSpace: 'nowrap' }}>{formatTs(stop.planDepart)}</Table.Cell>
                                    <Table.Cell>{(stop.actions ?? []).map((a, ai) => <Label key={ai} size="mini" color={a.kind === 'LOAD' ? 'green' : 'orange'}>{a.kind}</Label>)}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </div>
            </>
        )
    }

    function renderSegmentsTab() {
        if (segmentsLoading) return <div style={{ padding: 20 }}><Loader active inline>Computing…</Loader></div>

        if (segmentsFetchError) {
            return (
                <Message negative size="small">
                    <Icon name="warning sign" />
                    <strong>Something went wrong</strong>
                    <p style={{ marginTop: 4, wordBreak: 'break-all', fontSize: '0.88em' }}>{segmentsFetchError}</p>
                </Message>
            )
        }

        if (allBackendSegments.length > 0) {
            let standingCounter = 0
            return (
                <div style={{ overflowY: 'auto', maxHeight: MAP_HEIGHT - 60 }}>
                    {allBackendSegments.map((seg, idx) => {
                        const isStanding = seg.estimatedState === VehicleTrackSegment.EstimatedStateEnum.STANDING
                        const isDriving = seg.estimatedState === VehicleTrackSegment.EstimatedStateEnum.DRIVING
                        if (isStanding) standingCounter++
                        const num = isStanding ? standingCounter : null
                        const distKm = seg.distanceInM != null ? (seg.distanceInM / 1000).toFixed(2) : null
                        const durMin = seg.durationInSeconds != null ? (seg.durationInSeconds / 60).toFixed(1) : null
                        const isSelected = selectedBackendSegIdx === idx
                        const fromStr = seg.from ? new Date(seg.from).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'
                        const toStr = seg.to ? new Date(seg.to).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—'
                        return (
                            <div
                                key={idx}
                                onClick={() => handleBackendSegmentClick(idx, seg)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '6px 8px', marginBottom: 1,
                                    background: isSelected ? '#fff3cd' : 'transparent',
                                    border: isSelected ? '1px solid #f0c040' : '1px solid transparent',
                                    borderRadius: 3, cursor: 'pointer',
                                }}
                            >
                                <div style={{
                                    flexShrink: 0, width: 26, height: 26, borderRadius: '50%',
                                    background: isStanding ? '#00b5ad' : 'transparent',
                                    border: isDriving ? '2px solid #aaa' : 'none',
                                    color: isStanding ? 'white' : '#888',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    fontWeight: 'bold', fontSize: isDriving ? 13 : 11,
                                }}>
                                    {isStanding ? num : '>'}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontWeight: 600, fontSize: '0.85em', color: isStanding ? '#00b5ad' : '#333' }}>
                                        {isStanding ? 'Standing' : 'Driving'}
                                    </div>
                                    <div style={{ fontSize: '0.75em', color: '#777' }}>
                                        {fromStr} - {toStr}
                                        {(distKm || durMin) && <span> ({[distKm ? distKm + ' km' : null, durMin ? durMin + ' mins' : null].filter(Boolean).join(' ')})</span>}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )
        }

        return <Message info size="small"><p>Select a vehicle, then click <strong>📊 Seg</strong> in the map overlay to load segments from the backend.</p></Message>
    }

    function renderRightPanel() {
        const panes = [
            { menuItem: `Stops (${allStops.length})`, render: () => <Tab.Pane style={{ padding: 4, border: 0, boxShadow: 'none' }}>{renderStopsTab()}</Tab.Pane> },
            { menuItem: `Segments (${allBackendSegments.length})`, render: () => <Tab.Pane style={{ padding: 4, border: 0, boxShadow: 'none' }}>{renderSegmentsTab()}</Tab.Pane> },
        ]
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: MAP_HEIGHT, overflow: 'hidden' }}>
                <Tab
                    panes={panes}
                    activeIndex={rightTabIdx}
                    onTabChange={(_, { activeIndex }) => setRightTabIdx(activeIndex as number)}
                    menu={{ secondary: true, pointing: true, size: 'small' }}
                    style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
                />
            </div>
        )
    }

    // ── Render: GPS Coverage (full width below map) ────────────────────────────

    function renderGpsCoverageSection() {
        if (!dump || allSelectedPositions.length === 0 || !coverageTimeRange) return null
        return (
            <Segment style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 'bold' }}><Icon name="signal" />GPS Coverage</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <label style={{ color: '#555', fontSize: '0.88em' }}>Chunk size:</label>
                        <Button.Group size="mini">
                            {[5, 10, 15, 30, 60, 120].map(m => (
                                <Button
                                    key={m}
                                    active={coverageChunkMinutes === m}
                                    onClick={() => {
                                        setCoverageChunkMinutes(m)
                                        setCoveragePositions([])
                                        setCoverageChunk(null)
                                        setSelectedCoveragePosition(null)
                                    }}
                                >
                                    {m >= 60 ? `${m / 60}h` : `${m}m`}
                                </Button>
                            ))}
                        </Button.Group>
                    </span>
                </div>
                <DumpGpsCoverageBar
                    positions={allSelectedPositions}
                    from={coverageTimeRange.from}
                    to={coverageTimeRange.to}
                    chunkMinutes={coverageChunkMinutes}
                    selectedPosition={selectedCoveragePosition}
                    onPositionSelect={pos => {
                        setSelectedCoveragePosition(pos)
                        setFlyToPosition(pos)
                    }}
                    onChunkSelect={(positions, chunk) => {
                        setCoveragePositions(positions)
                        setCoverageChunk(chunk)
                        setSelectedCoveragePosition(null)
                    }}
                />
            </Segment>
        )
    }


    // ── Main render ────────────────────────────────────────────────────────────

    return (
        <div style={{ padding: '6px 0' }}>
            <Header as="h4" style={{ marginBottom: 6, marginTop: 0 }}>
                <Icon name="map" />
                <Header.Content>
                    Dump File Visualizer
                    <Header.Subheader style={{ fontSize: '0.78em' }}>
                        Upload a <code>_dump.json</code> — GPS track, stops, segments (from backend), GPS coverage.
                    </Header.Subheader>
                </Header.Content>
            </Header>

            {error && <Message negative compact size="small"><Icon name="warning" />{error}</Message>}

            {renderTopBar()}

            {/* Map + resizable right panel */}
            <div ref={containerRef} style={{ display: 'flex', alignItems: 'stretch', userSelect: isDraggingRef.current ? 'none' : 'auto' }}>
                {/* Map */}
                <div style={{ width: `${mapWidthPct}%`, minWidth: 0 }}>
                    {renderMap()}
                </div>

                {/* Draggable divider */}
                <div
                    onMouseDown={handleDividerMouseDown}
                    style={{
                        width: 6,
                        background: '#d0d0d0',
                        cursor: 'col-resize',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#a0a0a0')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#d0d0d0')}
                    title="Drag to resize"
                >
                    <span style={{ color: '#999', fontSize: 10, writingMode: 'vertical-rl', letterSpacing: 2 }}>⋮⋮</span>
                </div>

                {/* Right panel */}
                <div style={{ flex: 1, minWidth: 200, overflow: 'hidden' }}>
                    {dump ? renderRightPanel() : (
                        <div style={{ height: MAP_HEIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#888', fontSize: '0.9em' }}>
                            Upload a dump file to view data
                        </div>
                    )}
                </div>
            </div>

            {/* GPS Coverage — full width below */}
            {renderGpsCoverageSection()}
        </div>
    )
}

